import React, { useState, useEffect } from "react";
import DistillationExperiment from "../../api/Distillation/DistillationExperiment";
import DistillationStatus from "../../api/Distillation/DistillationStatus";
import {
  FilterButton,
  GlobalSecondaryButton,
  GlobalTabCollection,
  StyledTab,
} from "../../pages/styles";
import { Box } from "@mui/material";
import TableDistillationsBacklog from "./TableDistillationBacklog";
import FilterMenu from "../../components/FilterMenu";
import TableCompletedExperiments from "./TableAllCompletedExperiments";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { applyFiltersToArray, exportToCsv, formatMidasNumber } from "../../global";

const ManageDistillations = () => {
  const [tabValue, setTabValue] = useState(0);
  const [filteringOpen, setFilteringOpen] = useState(false);
  const [filters, setFilters] = useState([
    { name: null, displayName: null, operator: null, enumValues: [], value: "" },
  ]);

  const [myExperiments, setMyExperiments] = useState([]);
  const [myCompletedExperiments, setMyCompletedExperiments] = useState([]);

  const [distillationStatuses, setDistillationStatuses] = useState([]);
  const [isExperimentsLoading, setIsExperimentsLoading] = useState(true);
  const [isCompletedLoading, setIsCompletedLoading] = useState(true);

  const filterOptions = [
    { name: "id", displayName: "Distillation #", type: "string", enumValues: [] },
    {
      name: "initialContainer.sampleName",
      displayName: "Feed MIDAS #",
      type: "midasNumber",
      enumValues: [],
    }, //this is initialContainer.samplename
    { name: "requestedByEmail", displayName: "Requester", type: "string", enumValues: [] },
    { name: "createdDate", displayName: "Request Date", type: "date", enumValues: [] },
    { name: "mnemonic", displayName: "Mnemonic", type: "string", enumValues: [] },
    { name: "comments", displayName: "Comments", type: "string", enumValues: [] },
    { name: "experimentStartDate", displayName: "Start Date", type: "date", enumValues: [] },
    { name: "createdDate", displayName: "Submitted On", type: "date", enumValues: [] },
    {
      name: "requestedCompletionDate",
      displayName: "Req. Completion Date",
      type: "string",
      enumValues: [],
    },
    {
      name: "distillationStatusName",
      displayName: "Status",
      type: "enum",
      enumValues: distillationStatuses,
    },
  ];

  //get all the API info
  useEffect(() => {
    let cancelPromise = false;

    getOpenExperiments();

    DistillationExperiment.getCompletedExperiments().then((res) => {
      setIsCompletedLoading(false);
      if (cancelPromise) return;

      if (res.message === "Success") {
        setMyCompletedExperiments(
          res.result.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
        );
      } else {
        setMyCompletedExperiments([]);
      }
    });

    DistillationStatus.getAllDistillationStatuses().then((res) => {
      if (cancelPromise) return;
      setDistillationStatuses(
        res
          .filter((result) => result.isActive === true)
          .map((item) => item.distillationStatusName)
          .sort(),
      );
    });

    return () => {
      cancelPromise = true;
    };
  }, []);

  const getOpenExperiments = async () => {
    let cancelPromise = false;

    DistillationExperiment.getOpenExperiments().then((res) => {
      setIsExperimentsLoading(false);
      if (cancelPromise) return;

      if (res.message === "Success") {
        setMyExperiments(
          res.result.sort(
            (a, b) =>
              b.distillationPriorityName - a.distillationPriorityName ||
              new Date(a.createdDate) - new Date(b.createdDate),
          ),
        );
      } else {
        setMyExperiments([]);
      }
    });
  };

  const handleChange = (event, newValue) => {
    clearFiltersClick();
    setTabValue(newValue);
  };

  function closeFiltering() {
    setFilteringOpen(false);
  }

  function applyFilters() {
    setFilteringOpen(false);
  }

  const filterClick = (event) => {
    setFilteringOpen(true);
  };

  const clearFiltersClick = (event) => {
    setFilters([{ name: null, displayName: null, operator: null, enumValues: [], value: "" }]);
  };

  const renderSearchTab = () => {
    switch (tabValue) {
      case 0: {
        return (
          <TableDistillationsBacklog
            filters={filters}
            myExperiments={myExperiments}
            isDataLoading={isExperimentsLoading}
            getOpenExperiments={getOpenExperiments}
          />
        );
      }

      case 1: {
        return (
          <TableCompletedExperiments
            filters={filters}
            myExperiments={myCompletedExperiments}
            isDataLoading={isCompletedLoading}
          />
        );
      }
      default: {
        alert(tabValue);
      }
    }
  };

  const renderFilterMenu = () => {
    switch (tabValue) {
      case 0: {
        return (
          <FilterMenu
            moduleName="manage-distillations-backlog"
            open={filteringOpen}
            setOpen={setFilteringOpen}
            applyBtnAction={applyFilters}
            cancelButtonAction={closeFiltering}
            filteringInfo={filterOptions}
            appliedFilters={filters}
            setAppliedFilters={setFilters}
          />
        );
      }
      case 1: {
        return (
          <FilterMenu
            moduleName="manage-distillations-completed-experiments"
            open={filteringOpen}
            setOpen={setFilteringOpen}
            applyBtnAction={applyFilters}
            cancelButtonAction={closeFiltering}
            filteringInfo={filterOptions}
            appliedFilters={filters}
            setAppliedFilters={setFilters}
          />
        );
      }

      default: {
        alert(tabValue);
      }
    }
  };

  const renderExportCSV = () => {
    switch (tabValue) {
      case 0: {
        return (
          <>
            <FileDownloadIcon
              sx={{ m: 1 }}
              style={{ cursor: "pointer" }}
              onClick={(e) =>
                exportToCsv(
                  applyFiltersToArray(filters, myExperiments).map((container) => {
                    return {
                      ID: container.id,
                      Type: container.distillationTypeName,
                      "Feed Midas Number": formatMidasNumber(
                        container?.initialContainer?.sampleName,
                      ),
                      Requester: container.requestedByEmail,
                      Mnemonic: container.mnemonic,
                      Comments: container.comments,
                      "Start Date": container.experimentStartDate,
                      "Submitted On": container.createdDate,
                      "Req Completion Date": container.requestedCompletionDate,
                      Status: container.distillationStatusName,
                    };
                  }),
                  "Open Experiments",
                )
              }
            />
          </>
        );
      }
      case 1: {
        return (
          <>
            <FileDownloadIcon
              sx={{ m: 1 }}
              style={{ cursor: "pointer" }}
              onClick={(e) =>
                exportToCsv(
                  applyFiltersToArray(filters, myCompletedExperiments).map((container) => {
                    return {
                      ID: container.id,
                      Type: container.distillationTypeName,
                      "Feed Midas Number": formatMidasNumber(
                        container?.initialContainer?.sampleName,
                      ),
                      Requester: container.requestedByEmail,
                      Mnemonic: container.mnemonic,
                      Comments: container.comments,
                      "Start Date": container.experimentStartDate,
                      "Submitted On": container.createdDate,
                      "Req Completion Date": container.requestedCompletionDate,
                      Status: container.distillationStatusName,
                    };
                  }),
                  "Completed Experiments",
                )
              }
            />
          </>
        );
      }
      default:
        break;
    }
  };

  return (
    <div>
      <Box sx={{ bgcolor: "#fff", pt: 3, pb: 1 }} display="flex">
        <GlobalTabCollection
          style={{ marginRight: "1rem" }}
          scrollButtons="auto"
          variant="scrollable"
          value={tabValue}
          onChange={handleChange}
          aria-label="ant example">
          <StyledTab label="Open Experiments" />
          <StyledTab label="Completed Experiments" />
        </GlobalTabCollection>

        <Box display="flex" alignItems={"center"} marginLeft="auto" marginTop="-.8rem">
          <FilterButton
            variant="contained"
            filtered={!(filters[0].name === null)}
            onClick={() => filterClick()}>
            Filters
          </FilterButton>

          {!(filters[0].name === null) && (
            <GlobalSecondaryButton
              variant="contained"
              style={{ marginLeft: "1rem" }}
              onClick={() => clearFiltersClick()}>
              Clear Filters
            </GlobalSecondaryButton>
          )}

          {renderExportCSV()}
        </Box>
      </Box>
      {renderFilterMenu()}
      {renderSearchTab()}
    </div>
  );
};

export default ManageDistillations;
